import { useMemo } from 'react';
import blogData from '../data/blog-posts.json';
import type { BlogPost } from '../types';

// Cache for loaded content
const contentCache = new Map<string, string>();

export const loadBlogContent = async (contentPath: string): Promise<{ content: string } | null> => {
    try {
        if (!contentPath) {
            console.warn('No filename provided to loadBlogContent');
            return null;
        }

        console.log('Loading content for:', contentPath);
        const post = blogData.posts.find(post => post.contentPath === contentPath);
        if (!post) {
            console.warn(`No post found for filename: ${contentPath}`);
            return null;
        }

        // Load the TypeScript content
        const content = await loadPostContent(contentPath);
        if (!content) {
            return null;
        }
        
        return {
            content
        };
    } catch (error) {
        console.error('Error loading blog content:', error);
        return null;
    }
};

export const loadPostContent = async (filename: string): Promise<string | null> => {
    try {
        if (contentCache.has(filename)) {
            return contentCache.get(filename) || null;
        }

        // Try loading as a direct .ts file first
        try {
            console.log(`Trying to load ${filename}.ts`);
            const module = await import(`../posts/${filename}.ts`);
            if (module.default?.content) {
                console.log(`Successfully loaded ${filename}.ts`);
                contentCache.set(filename, module.default.content);
                return module.default.content;
            }
        } catch (error) {
            console.log(`No direct .ts file found for ${filename}, trying folder structure`);
        }

        // If direct file fails, try loading from folder structure
        try {
            console.log(`Trying to load ${filename}/index.ts`);
            const module = await import(`../posts/${filename}/index.ts`);
            if (module.default?.content) {
                console.log(`Successfully loaded ${filename}/index.ts`);
                contentCache.set(filename, module.default.content);
                return module.default.content;
            }
        } catch (error) {
            console.error(`Could not load content for ${filename} from either direct file or folder structure`);
            return null;
        }

        return null;
    } catch (error) {
        console.error('Error loading post content:', error);
        return null;
    }
};

/**
 * Attempts to load a visualization component for a specific blog post
 * Checks both the post's directory and the global visualizations directory
 */
export const loadVisualization = async (postId: string, vizName: string): Promise<React.ComponentType | null> => {
    try {
        console.log(`Attempting to load visualization: ${vizName} for post: ${postId}`);
        
        // First, try loading from post-specific directory
        const post = blogData.posts.find(p => p.id === postId);
        if (post) {
            try {
                // Try loading from post-specific directory
                console.log(`Trying post-specific path: ../posts/${post.contentPath}/visualizations/${vizName}`);
                const module = await import(`../posts/${post.contentPath}/visualizations/${vizName}`);
                if (module.default) {
                    console.log(`Successfully loaded visualization from post directory`);
                    return module.default;
                }
            } catch (error) {
                console.log(`No visualization found in post directory, trying global directory`);
            }
        }
        
        // Fall back to global visualizations directory
        try {
            console.log(`Trying global path: ../visualizations/${vizName}`);
            const module = await import(`../visualizations/${vizName}`);
            if (module.default) {
                console.log(`Successfully loaded visualization from global directory`);
                return module.default;
            }
        } catch (error) {
            console.error(`Could not load visualization ${vizName} from either location:`, error);
            
            // Try without extension as last resort
            if (vizName.endsWith('.jsx') || vizName.endsWith('.tsx')) {
                const baseVizName = vizName.replace(/\.(jsx|tsx)$/, '');
                try {
                    console.log(`Trying global path without extension: ../visualizations/${baseVizName}`);
                    const module = await import(`../visualizations/${baseVizName}`);
                    if (module.default) {
                        console.log(`Successfully loaded visualization without extension`);
                        return module.default;
                    }
                } catch (innerError) {
                    console.error(`Could not load visualization without extension either:`, innerError);
                }
            }
        }
        
        return null;
    } catch (error) {
        console.error(`Error loading visualization ${vizName}:`, error);
        return null;
    }
};

// Export the blog data for direct access
export const getBlogData = (): BlogPost[] => {
    return blogData.posts.map(post => ({
        ...post,
        isPasswordProtected: post.isPasswordProtected ?? false,
        visualization: post.visualization,
        metadata: {
            author: post.metadata.author,
            readTime: post.metadata.readTime.toString(),
            featured: post.metadata.featured ?? false,
            series: post.metadata.series || '',
            links: post.metadata.links || [],
            media: {
                images: post.metadata.media?.images || [],
                videos: post.metadata.media?.videos || [],
                codeSnippets: post.metadata.media?.codeSnippets || []
            }
        }
    }));
};